import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { Checkbox, Chip, FormControlLabel, IconButton, TextField, Radio, RadioGroup } from "@mui/material";
import { useContext, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../Component/Modal/Modal";
import MUISelect from "../../../Component/UI/MUISelect/MUISelect";
import MUIMultiSelect from "../../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import { useSelector } from "react-redux";
import { CategoryAnalysisContext } from "../category-analysis-context";
import TemplateSetting from "../../BrandAudit/BrandAuditSetting/TemplateSetting";
import PersonaSetting from "../../BrandAudit/BrandAuditSetting/PersonaSetting";
import classes from "../../BrandAudit/BrandAuditSetting/Setting.module.css";
import { getMetricOptions } from "../metricOptions";
import { AuthContext } from "../../../context/auth-context";
import { resolveDefaultKeywordOptions, resolveMaxKeywords } from "../../../utils/keywordOptions";
import { hasBatchCapableLlm } from "../../../utils/batchCapableLlms";

const MAX_RANK_COMPETITIVE_SET = 10;

export default function CASettingsModal({
  open,
  onClose,
  category,
  categoryWiseMetric,
  metricSettingId,
  onCategoryTemplateChange,
  MetricSettingChangeHandle,
  personaSettings,
  setPersonaSettings,
  rbiType,
  setRbiType,
  keywords,
  setKeywords,
  selectedMetrics,
  setSelectedMetrics,
  isBatchMode = false,
  onBatchModeChange = () => {},
  llm = [],
  rankCompetitiveSet = [],
  onRankCompetitiveSetChange = () => {},
  rankMethodology = "by_category",
  onRankMethodologyChange = () => {},
  competitorsData = {},
  keyPlayers = {},
  keyPlayersLoading = false,
}) {
  const caCtx = useContext(CategoryAnalysisContext);
  const authCtx = useContext(AuthContext);
  const { customerUiInfo, user } = useContext(AuthContext);
  const userMetricSetting = useSelector((store) => store.userSet);
  const METRIC_OPTIONS = getMetricOptions().filter(
    (option) =>
      !["nonBrandedQuestions", "factBasedQuestions"].includes(option.value) ||
      !!customerUiInfo?.is_non_branded_questions
  );

  const isFinalTransactionCompleted =
    caCtx?.state?.finalTransactionStatus === "COMPLETED" ||
    caCtx?.state?.finalTransactionStatus === "RETRY";
  const isDisabled = caCtx?.loading || isFinalTransactionCompleted;

  const templateOptions =
    userMetricSetting?.data?.map((ele) => ({
      name: ele.name,
      value: ele.id,
      isDefault: ele.isDefault,
    })) ?? [];

  const defaultTemplate = templateOptions.find((opt) => opt.isDefault)?.name || "Standard Audit V2";

  const selectedTemplateData =
    (userMetricSetting?.data ?? []).find((ele) => ele.id == metricSettingId) ?? {};

  const QUESTIONS_METRIC_ATTRIBUTE_TYPE = {
    nonBrandedQuestions: "non-branded-questions",
    factBasedQuestions: "fact-based-questions",
  };
  const QUESTIONS_METRIC_LABEL = {
    nonBrandedQuestions: "Open Ended Questions",
    factBasedQuestions: "Fact Based Questions",
  };

  const handleSelectedMetricsChange = (newValues) => {
    if (newValues.length === 0) return;
    const blocked = newValues.filter(
      (value) =>
        QUESTIONS_METRIC_ATTRIBUTE_TYPE[value] &&
        !(selectedTemplateData?.[QUESTIONS_METRIC_ATTRIBUTE_TYPE[value]]?.length > 0)
    );
    if (blocked.length > 0) {
      blocked.forEach((value) => {
        toast.error(`The selected template has no ${QUESTIONS_METRIC_LABEL[value]} configured. Add some in Template Settings first.`);
      });
      newValues = newValues.filter((value) => !blocked.includes(value));
      if (newValues.length === 0) return;
    }
    setSelectedMetrics(newValues);
  };

  const overrideCount = category.filter(
    (cat) => categoryWiseMetric[cat] && Number(categoryWiseMetric[cat]) !== Number(metricSettingId)
  ).length;

  const defaultKeywordOptions = resolveDefaultKeywordOptions(customerUiInfo);
  const maxKeywords = resolveMaxKeywords(user?.email, defaultKeywordOptions.length);
  const [customKeywordInput, setCustomKeywordInput] = useState("");

  const allKeywordOptions = [
    ...defaultKeywordOptions,
    ...keywords.filter((k) => !defaultKeywordOptions.includes(k)),
  ];

  const handleKeywordToggle = (keyword, checked) => {
    if (checked) {
      if (keywords.length >= maxKeywords) return;
      setKeywords((prev) => [...prev, keyword]);
    } else {
      setKeywords((prev) => prev.filter((k) => k !== keyword));
    }
  };

  const handleAddCustomKeyword = () => {
    const trimmed = customKeywordInput.trim();
    if (!trimmed) return;
    if (allKeywordOptions.some((k) => k.toLowerCase() === trimmed.toLowerCase())) return;
    if (keywords.length >= maxKeywords) {
      setKeywords((prev) => [...prev.slice(0, maxKeywords - 1), trimmed]);
    } else {
      setKeywords((prev) => [...prev, trimmed]);
    }
    setCustomKeywordInput("");
  };

  const handleRemoveCustomKeyword = (keyword) => {
    setKeywords((prev) => prev.filter((k) => k !== keyword));
  };

  const lockedRankNames = Array.from(
    new Set(
      Object.values(competitorsData || {})
        .flat()
        .map((c) => c?.name)
        .filter(Boolean)
    )
  );
  const additionalRankSelected = (rankCompetitiveSet || []).filter(
    (name) => !lockedRankNames.includes(name)
  );
  const maxAdditionalRank = Math.max(0, MAX_RANK_COMPETITIVE_SET - lockedRankNames.length);

  const rankCompetitiveSetOptions = (() => {
    const apiPlayers = Object.values(keyPlayers || {})
      .flat()
      .map((player) => ({ name: player.name, value: player.name }))
      .filter((p) => !lockedRankNames.includes(p.value));
    const existingSelected = additionalRankSelected
      .filter((name) => !apiPlayers.some((p) => p.value === name))
      .map((name) => ({ name, value: name }));
    return [...existingSelected, ...apiPlayers];
  })();

  const handleRankCompetitiveSetChange = (selected) => {
    const cleaned = selected.filter((n) => !lockedRankNames.includes(n));
    if (cleaned.length > maxAdditionalRank) {
      onRankCompetitiveSetChange(cleaned.slice(0, maxAdditionalRank));
      return;
    }
    onRankCompetitiveSetChange(cleaned);
  };

  return (
    <Modal
      handleClose={onClose}
      open={open}
      title="Configuration Settings"
      width="750"
      showActions={false}
    >
      <div className={classes.modalContent}>
        <div className={classes.modalBody}>
          {/* Batch Mode — only visible for users with is_batch_mode_enabled */}
          {!!user?.is_batch_mode_enabled && hasBatchCapableLlm(llm) && (
            <div className={classes.settingSection}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isBatchMode}
                    onChange={(e) => onBatchModeChange(e.target.checked)}
                    disabled={isDisabled}
                    sx={{
                      color: "#d1d5db",
                      "&.Mui-checked": { color: "#2dd4bf" },
                      padding: "4px",
                    }}
                  />
                }
                label="Batch Mode"
                sx={{
                  ".MuiFormControlLabel-label": {
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  },
                }}
              />
            </div>
          )}

          {/* Template Settings */}
          <div className={classes.settingSection}>
            <TemplateSetting
              templateOptions={templateOptions}
              metricSettingId={metricSettingId === "default" ? "default" : Number(metricSettingId)}
              MetricSettingChangeHandle={MetricSettingChangeHandle}
              category={category}
              categoryWiseMetric={categoryWiseMetric}
              onCategoryTemplateChange={onCategoryTemplateChange}
              defaultTemplate={defaultTemplate}
              overrideCount={overrideCount}
              showAddButton={!!authCtx?.user?.is_template_setting_enabled}
              isDisabled={isDisabled}
              metricSettingObject={caCtx.state?.metricSettingObject}
            />
          </div>

          {/* Persona Settings */}
          <div className={classes.settingSection}>
            <PersonaSetting
              personaSettings={personaSettings}
              setPersonaSettings={setPersonaSettings}
            />
          </div>

          {/* NOTE: Conversation section is intentionally omitted for Category Analysis */}

          {/* RBI Type */}
          <div className={classes.settingSection}>
            <h4 className={classes.settingSectionTitle}>RBI Type</h4>
            <MUISelect
              fullWidth={true}
              optionValue={rbiType}
              disabled={isDisabled}
              options={[
                { name: "Propensity To Recommend", value: "propensityToRecommend" },
                { name: "Purchase Intent", value: "propensityToBuy" },
              ]}
              onChange={(e) => setRbiType(e.target.value)}
            />
          </div>

          {/* Metrics to Run */}
          <div className={classes.settingSection}>
            <h4 className={classes.settingSectionTitle}>Metrics to Run</h4>
            <MUIMultiSelect
              options={METRIC_OPTIONS}
              value={selectedMetrics}
              disabled={isDisabled}
              placeholder="Select metrics"
              onChange={handleSelectedMetricsChange}
            />
          </div>

          {/* Keywords / Rank Attributes - only relevant when Rank metric is selected */}
          {selectedMetrics.includes("rank") && (
            <div className={classes.settingSection}>
              <h4 className={classes.settingSectionTitle}>
                Rank Attributes
                <span className={classes.keywordCount}>({keywords.length}/{maxKeywords} selected)</span>
              </h4>
              <div className={classes.keywordsContainer}>
                {defaultKeywordOptions.map((keyword) => (
                  <FormControlLabel
                    key={keyword}
                    control={
                      <Checkbox
                        checked={keywords.includes(keyword)}
                        onChange={(e) => handleKeywordToggle(keyword, e.target.checked)}
                        disabled={isDisabled || (!keywords.includes(keyword) && keywords.length >= maxKeywords)}
                        sx={{
                          color: "#d1d5db",
                          "&.Mui-checked": { color: "#2dd4bf" },
                          padding: "4px",
                        }}
                      />
                    }
                    label={keyword}
                    sx={{
                      "&.MuiFormControlLabel-root": {
                        padding: "0.5rem 1rem",
                        border: "1px solid #e5e7eb",
                        borderRadius: "0.75rem",
                        margin: "0 !important",
                        background: keywords.includes(keyword) ? "#f0fdfa" : "white",
                      },
                      ".MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 },
                    }}
                  />
                ))}
              </div>

              {keywords.filter((k) => !defaultKeywordOptions.includes(k)).length > 0 && (
                <div className={classes.customKeywordsChips}>
                  {keywords
                    .filter((k) => !defaultKeywordOptions.includes(k))
                    .map((keyword) => (
                      <Chip
                        key={keyword}
                        label={keyword}
                        onDelete={isDisabled ? undefined : () => handleRemoveCustomKeyword(keyword)}
                        deleteIcon={<CloseIcon style={{ fontSize: "16px" }} />}
                        sx={{
                          background: "#f0fdfa",
                          border: "1px solid #2dd4bf",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                        }}
                      />
                    ))}
                </div>
              )}

              <div className={classes.customKeywordInput}>
                <TextField
                  size="small"
                  placeholder="Add custom Attribute..."
                  value={customKeywordInput}
                  onChange={(e) => setCustomKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCustomKeyword();
                    }
                  }}
                  disabled={isDisabled}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  onClick={handleAddCustomKeyword}
                  disabled={isDisabled || !customKeywordInput.trim()}
                  sx={{
                    border: "1px solid #d1d5db",
                    borderRadius: "0.5rem",
                    color: "#2dd4bf",
                    "&:hover": { background: "#f0fdfa" },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          )}

          {/* Rank Methodology — only when the user has this feature enabled */}
          {selectedMetrics.includes("rank") && !!user?.is_custom_rank && (
            <div className={classes.settingSection}>
              <h4 className={classes.settingSectionTitle}>Rank Methodology</h4>
              <RadioGroup
                value={rankMethodology}
                onChange={(e) => onRankMethodologyChange(e.target.value)}
              >
                <FormControlLabel
                  value="by_category"
                  control={<Radio disabled={isDisabled} sx={{ "&.Mui-checked": { color: "#2dd4bf" } }} />}
                  label="By Category"
                  sx={{ ".MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
                />
                <FormControlLabel
                  value="by_named_competitor"
                  control={<Radio disabled={isDisabled} sx={{ "&.Mui-checked": { color: "#2dd4bf" } }} />}
                  label="By Named Competitor"
                  sx={{ ".MuiFormControlLabel-label": { fontSize: "0.875rem", fontWeight: 600 } }}
                />
              </RadioGroup>
            </div>
          )}

          {!!user?.is_custom_rank && (
            <div className={classes.settingSection}>
              <h4 className={classes.settingSectionTitle}>
                Competitive Set
                <span className={classes.keywordCount}>
                  ({lockedRankNames.length + additionalRankSelected.length}/{MAX_RANK_COMPETITIVE_SET} selected)
                </span>
              </h4>
              {lockedRankNames.length > 0 && (
                <div className={classes.customKeywordsChips} style={{ marginBottom: "0.5rem" }}>
                  {lockedRankNames.map((name) => (
                    <Chip
                      key={name}
                      label={name}
                      title="Selected on the form — always included"
                      sx={{
                        background: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      }}
                    />
                  ))}
                </div>
              )}
              <MUIMultiSelect
                loading={keyPlayersLoading}
                disabled={isDisabled}
                fullWidth={true}
                optionValue={""}
                label=""
                placeholder="Select additional competitors"
                value={additionalRankSelected}
                options={rankCompetitiveSetOptions}
                allowSettingPrimary={false}
                shouldShowValue={true}
                allowCustomOption={!isDisabled}
                onChange={handleRankCompetitiveSetChange}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
